import { FC, useMemo } from 'react';
import { Plus, TrashSimple } from '@phosphor-icons/react';
import {
  IBlockBase,
  IBlockTrack,
  ICoachSessionAthlete,
  IDidacticClassification,
} from '../../../endpoints/coachSessions';
import { ISportTaxonomy } from '../../../models/trainingStatistics';
import { BlockBase } from './BlockBase';
import BlockClassification from '../../../components/ProgrammingPage/BlockClassification/BlockClassification';
import styles from './BlockBase.module.scss';
import trackStyles from './TrackBlock.module.scss';

interface RotationBlockProps {
  block: IBlockBase;
  index: number;
  onChange: (patch: Partial<IBlockBase>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  sport?: string | null;
  taxonomy?: ISportTaxonomy | null;
  totalBlocks?: number;
  rosterAthletes?: ICoachSessionAthlete[];
}

const WORK_OWNER_LABELS: Record<string, string> = {
  coach: 'Coach',
  athletic_trainer: 'Preparatore',
  goalkeeping_coach: 'Coach portieri',
};

/**
 * Tipo 3 — Rotazione.
 * Come il parallelo, ma i gruppi si scambiano dopo N minuti.
 * Spec §4.3.
 */
export const RotationBlock: FC<RotationBlockProps> = ({
  block,
  index,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  sport,
  taxonomy,
  totalBlocks,
  rosterAthletes,
}) => {
  const tracks: IBlockTrack[] = block.tracks ?? [
    { name: 'Gruppo A', exerciseName: '' },
    { name: 'Gruppo B', exerciseName: '' },
  ];

  const athleteNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of rosterAthletes ?? []) {
      const full = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
      if (a.athleteId && full) map.set(a.athleteId, full);
    }
    return map;
  }, [rosterAthletes]);

  const formatAthlete = (id: string) =>
    athleteNameById.get(id) ?? `…${id.slice(-6)}`;

  const updateTrack = (tIdx: number, patch: Partial<IBlockTrack>) => {
    const updated = tracks.map((t, i) =>
      i === tIdx ? { ...t, ...patch } : t,
    );
    onChange({ tracks: updated });
  };

  const addTrack = () => {
    if (tracks.length >= 4) return;
    onChange({
      tracks: [
        ...tracks,
        { name: `Gruppo ${String.fromCharCode(65 + tracks.length)}`, exerciseName: '' },
      ],
    });
  };

  const removeTrack = (tIdx: number) => {
    if (tracks.length <= 2) return;
    onChange({ tracks: tracks.filter((_, i) => i !== tIdx) });
  };

  /**
   * Bug 6a01eeeb — D&D atleti dal RosterPanel verso le drop zone dei track.
   * RosterPanel imposta `e.dataTransfer.setData('athleteId', ...)`.
   * Su drop: rimuove l'athleteId da TUTTI gli altri track (no overlap)
   * e lo aggiunge al track destinazione.
   */
  const handleDropAthlete = (
    e: React.DragEvent<HTMLDivElement>,
    targetIdx: number,
  ) => {
    e.preventDefault();
    const athleteId = e.dataTransfer.getData('athleteId');
    if (!athleteId) return;
    const next = tracks.map((t, i) => {
      const current = (t.assignedAthleteIds ?? []).filter((id) => id !== athleteId);
      if (i === targetIdx && !current.includes(athleteId)) {
        return { ...t, assignedAthleteIds: [...current, athleteId] };
      }
      return { ...t, assignedAthleteIds: current };
    });
    onChange({ tracks: next });
  };

  const removeAthleteFromTrack = (tIdx: number, athleteId: string) => {
    const next = tracks.map((t, i) =>
      i === tIdx
        ? { ...t, assignedAthleteIds: (t.assignedAthleteIds ?? []).filter((id) => id !== athleteId) }
        : t,
    );
    onChange({ tracks: next });
  };

  const totalDuration =
    (block.rotationDurationMinutes ?? 0) * (block.numberOfRotations ?? 1);

  return (
    <BlockBase
      index={index}
      blockType="rotation"
      title={
        block.exerciseName ??
        `Rotazione ×${block.numberOfRotations ?? 2} · ${block.rotationDurationMinutes ?? 10}' cad.`
      }
      durationMinutes={totalDuration || block.durationMinutes}
      transitionAfterMinutes={block.transitionAfterMinutes}
      onTransitionChange={(v) => onChange({ transitionAfterMinutes: v })}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {/* Rotation params */}
      <div className={styles.fieldGrid} style={{ marginBottom: 12 }}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Durata turno (min)</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={1}
            value={block.rotationDurationMinutes ?? ''}
            onChange={(e) =>
              onChange({
                rotationDurationMinutes: Number(e.target.value) || undefined,
              })
            }
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>N. rotazioni</label>
          <input
            className={styles.fieldInput}
            type="number"
            min={1}
            value={block.numberOfRotations ?? ''}
            onChange={(e) =>
              onChange({
                numberOfRotations: Number(e.target.value) || undefined,
              })
            }
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Tot. durata (min)</label>
          <input
            className={styles.fieldInput}
            value={totalDuration || '—'}
            readOnly
            style={{ background: '#f9f9f9', color: '#888' }}
          />
        </div>
      </div>

      {/* Track list */}
      <div className={trackStyles.trackList}>
        {tracks.map((track, tIdx) => (
          <div key={tIdx} className={trackStyles.trackCard}>
            <div className={trackStyles.trackHeader}>
              <input
                className={trackStyles.trackNameInput}
                value={track.name}
                onChange={(e) => updateTrack(tIdx, { name: e.target.value })}
                placeholder={`Gruppo ${String.fromCharCode(65 + tIdx)}`}
              />
              <select
                className={trackStyles.trackOwnerSelect}
                value={track.workOwner ?? 'coach'}
                onChange={(e) =>
                  updateTrack(tIdx, { workOwner: e.target.value as any })
                }
              >
                {Object.entries(WORK_OWNER_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              {tracks.length > 2 && (
                <button
                  type="button"
                  className={trackStyles.removeTrackBtn}
                  onClick={() => removeTrack(tIdx)}
                  title="Rimuovi gruppo"
                >
                  <TrashSimple size={13} />
                </button>
              )}
            </div>
            <input
              className={trackStyles.trackExerciseInput}
              value={track.exerciseName ?? ''}
              onChange={(e) =>
                updateTrack(tIdx, { exerciseName: e.target.value })
              }
              placeholder="Esercitazione turno 1…"
            />
            <div
              className={trackStyles.trackDropZone}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => handleDropAthlete(e, tIdx)}
            >
              {(track.assignedAthleteIds ?? []).length === 0 ? (
                <span className={trackStyles.trackDropLabel}>
                  Trascina atleti qui (assegnazione iniziale)
                </span>
              ) : (
                <div className={trackStyles.trackAthleteChips}>
                  {(track.assignedAthleteIds ?? []).map((aid) => (
                    <span
                      key={aid}
                      className={trackStyles.trackAthleteChip}
                      onClick={() => removeAthleteFromTrack(tIdx, aid)}
                      title="Rimuovi"
                    >
                      {formatAthlete(aid)} ×
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {tracks.length < 4 && (
          <button
            type="button"
            className={trackStyles.addTrackBtn}
            onClick={addTrack}
          >
            <Plus size={13} />
            Aggiungi track
          </button>
        )}
      </div>

      {sport && taxonomy && (
        <BlockClassification
          value={block.didacticClassification ?? {}}
          onChange={(c: IDidacticClassification) =>
            onChange({ didacticClassification: c })
          }
          taxonomy={taxonomy}
          sport={sport}
          blockData={{
            exerciseName: block.exerciseName,
            notes: block.notes,
            order: block.order,
            durationMinutes: block.durationMinutes,
          }}
          totalBlocks={totalBlocks}
        />
      )}
    </BlockBase>
  );
};
